import React from 'react';

import {
    SelectButton as LumXSelectButton,
    type SelectButtonProps as LumXSelectButtonProps,
    type SelectButtonTranslations,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useComboboxRenderOptionWithDataId } from '../../hooks/useComboboxRenderOptionWithDataId';

/** Get the translations for the combobox states and elements */
const useTranslations = (): SelectButtonTranslations => {
    const { translateKey, translateAndReplace, pluralize } = useTranslate();

    return React.useMemo(
        () => ({
            loadingMessage: translateKey(GLOBAL.LOADING),
            errorMessage: translateKey(GLOBAL.SERVICE_UNAVAILABLE),
            errorTryReloadMessage: translateKey(GLOBAL.TRY_RELOAD),
            nbOptionMessage: (n: number) =>
                translateAndReplace(pluralize(GLOBAL.NB_OPTIONS_AVAILABLE, n), { NB: n }) as string,
        }),
        [translateKey, translateAndReplace, pluralize],
    );
};

export type SelectButtonProps<
    O = any,
    E extends React.ElementType = React.ComponentType<any>,
    S extends 'single' | 'multiple' = 'single',
> = LumXSelectButtonProps<O, E, S> & {
    /** @deprecated Use `ref` instead. Forwarded to the trigger element.  */
    buttonRef?: React.Ref<HTMLButtonElement>;
};

const InnerSelectButton = React.forwardRef((props: SelectButtonProps, ref: React.Ref<unknown>) => {
    const { translations, buttonRef, renderOption, getOptionId, getOptionName, ...rest } = props;
    const defaults = useTranslations();
    const mergedTranslations = React.useMemo(() => ({ ...defaults, ...translations }), [defaults, translations]);
    const wrappedRenderOption = useComboboxRenderOptionWithDataId(renderOption, getOptionId, getOptionName);

    return (
        <LumXSelectButton
            {...(rest as any)}
            getOptionId={getOptionId}
            getOptionName={getOptionName}
            renderOption={wrappedRenderOption}
            translations={mergedTranslations}
            ref={(buttonRef ?? ref) as any}
        />
    );
});

type SelectButtonComponent = <
    O,
    E extends React.ElementType = React.ComponentType<any>,
    S extends 'single' | 'multiple' = 'single',
>(
    props: SelectButtonProps<O, E, S>,
) => React.ReactNode;

export const SelectButton: SelectButtonComponent = InnerSelectButton as unknown as SelectButtonComponent;
